import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MapPin, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { placesAutocomplete, placesDetails } from '@/lib/google-places';

interface LocationResult {
  country: 'CA' | 'US' | '' | string;
  countryCode: string;
  state: string;
  stateCode: string;
  city: string;
  formattedAddress: string;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onLocationSelect: (location: LocationResult | null) => void;
  placeholder?: string;
  language?: 'en' | 'fr';
}

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

export function LocationAutocomplete({
  value,
  onChange,
  onLocationSelect,
  placeholder = 'Search city, province, state...',
  language = 'en',
}: LocationAutocompleteProps) {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPredictions = useCallback(async (input: string) => {
    if (input.length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await placesAutocomplete({
        input,
        types: '(regions)',
        language,
        components: 'country:ca|country:us',
      });

      if (result.predictions && result.predictions.length > 0) {
        setPredictions(result.predictions);
        setShowDropdown(true);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error('Error fetching location predictions:', err);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 300);
  }, [onChange, fetchPredictions]);

  const handleSelectPlace = useCallback(async (prediction: Prediction) => {
    setIsLoading(true);
    setShowDropdown(false);
    onChange(prediction.description);

    try {
      const data = await placesDetails({ placeId: prediction.place_id, language });

      if (data.result && data.result.address_components) {
        const components = data.result.address_components;

        let city = '';
        let state = '';
        let stateCode = '';
        let country = '';
        let countryCode = '';

        components.forEach((component: { types: string[]; long_name: string; short_name: string }) => {
          const types = component.types;
          if (types.includes('locality') || types.includes('sublocality') || types.includes('postal_town')) {
            city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            state = component.long_name;
            stateCode = component.short_name;
          }
          if (types.includes('country')) {
            country = component.long_name;
            countryCode = component.short_name;
          }
        });

        if (!city) {
          const adminLevel2 = components.find((c: { types: string[] }) => c.types.includes('administrative_area_level_2'));
          if (adminLevel2) {
            city = adminLevel2.long_name;
          }
        }

        onLocationSelect({
          country: countryCode === 'CA' ? 'CA' : countryCode === 'US' ? 'US' : countryCode,
          countryCode,
          state,
          stateCode,
          city,
          formattedAddress: data.result.formatted_address || prediction.description,
        });
      }
    } catch (err) {
      console.error('Error fetching place details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [onChange, onLocationSelect, language]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, predictions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSelectPlace(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showDropdown, predictions, selectedIndex, handleSelectPlace]);

  const handleClear = useCallback(() => {
    onChange('');
    setPredictions([]);
    setShowDropdown(false);
    onLocationSelect(null);
    inputRef.current?.focus();
  }, [onChange, onLocationSelect]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="relative">
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => predictions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        className="pl-11 pr-10 h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white"
        autoComplete="off"
      />

      {/* Loading / Clear */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-[#b8924f] animate-spin" />
        ) : value ? (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 rounded-full hover:bg-muted transition-colors"
            tabIndex={-1}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        ) : null}
      </div>

      {/* Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPlace(prediction)}
              className={`w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-slate-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-muted' : ''
              }`}
            >
              <div className="text-sm font-medium text-foreground">
                {prediction.structured_formatting?.main_text || prediction.description.split(',')[0]}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {prediction.structured_formatting?.secondary_text ||
                  prediction.description.split(',').slice(1).join(',').trim()}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
